import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Package, ChevronRight, ShoppingBag, Truck, CheckCircle, Clock, XCircle, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
  confirmed: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Confirmed' },
  processing: { color: 'bg-purple-100 text-purple-700', icon: Package, label: 'Processing' },
  shipped: { color: 'bg-orange-100 text-orange-700', icon: Truck, label: 'Shipped' },
  delivered: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Delivered' },
  cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' }
};

export default function OrdersPage() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setOrders(await res.json());
      }
    } catch (e) {
      console.error('Error fetching orders:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading orders..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3">
            <Package className="w-8 h-8" />
            My Orders
          </h1>
          <p className="text-muted-foreground mt-2">
            Track and manage your orders
          </p>
        </div>

        {orders.length > 0 ? (
          <div className="space-y-4">
            {orders.map((order) => {
              const status = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              
              return (
                <div key={order.id} className="card-brutal p-6" data-testid={`order-${order.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
                      <StatusIcon className="w-4 h-4" />
                      {status.label}
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-primary/10 pt-4">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                            <ShoppingBag className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Tracking Information */}
                  {order.tracking_number && (
                    <div className="mt-4 p-4 rounded-lg bg-pastel-stickers/20 border border-pastel-stickers/30">
                      <p className="font-medium flex items-center gap-2">
                        <Truck className="w-5 h-5 text-orange-600" /> 
                        Tracking: {order.tracking_number}
                      </p>
                      {order.tracking_url && (
                        <a 
                          href={order.tracking_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-pastel-art hover:underline flex items-center gap-1 mt-2"
                        >
                          Track Your Package <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Status History */}
                  {order.status_history && order.status_history.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-primary/10">
                      <p className="text-sm font-medium mb-2">Status History</p>
                      <div className="space-y-2">
                        {order.status_history.slice().reverse().map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-sm">
                            <div className={`w-2 h-2 rounded-full ${
                              entry.status === 'delivered' ? 'bg-green-500' :
                              entry.status === 'shipped' ? 'bg-orange-500' :
                              entry.status === 'processing' ? 'bg-purple-500' :
                              entry.status === 'cancelled' ? 'bg-red-500' : 'bg-blue-500'
                            }`} />
                            <span className="capitalize font-medium">{entry.status}</span>
                            <span className="text-muted-foreground">
                              {new Date(entry.timestamp).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary/10">
                    <p className="font-heading font-bold text-lg">
                      Total: ${order.total?.toFixed(2)}
                    </p>
                    <Link to={`/orders/${order.id}`}>
                      <Button variant="ghost" size="sm">
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <Package className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-heading font-bold mb-2">No orders yet</h2>
            <p className="text-muted-foreground mb-6">
              When you make a purchase, it will appear here
            </p>
            <Link to="/browse">
              <Button className="btn-brutal-primary">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Start Shopping
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
